<template>
  <ProductListLayout
    :results-text="softwareCountInformation"
    :is-loading="isListLoading"
    :show-filter="true"
    :content-heading-id="
      $textDictionary['softwareCenter.list.contentHeadingId']
    "
    :content-heading="$textDictionary['softwareCenter.list.contentHeading']"
    :filters-skip-btn-text="
      $textDictionary['softwareCenter.filter.filtersSkipBtnText.label']
    "
    :filters-open-btn-text="
      $textDictionary['softwareCenter.filter.filtersOpenBtnText.label']
    "
    :filters-modal-title="
      $textDictionary['softwareCenter.filterModal.filtersModalTitle.label']
    "
    :filters-modal-close-icon-text="
      $textDictionary[
        'softwareCenter.filterModal.filtersModalCloseIconText.label'
      ]
    "
    :filters-modal-apply-btn-text="
      $textDictionary[
        'softwareCenter.filterModal.filtersModalApplyBtnText.label'
      ]
    "
  >
    <template v-if="hasLoadedFilters" #filter>
      <SoftwareCenterFilters />
    </template>
    <template v-if="store.filters" #filterTags>
      <HeadlineModule
        v-if="store.isInitialView"
        :level="4"
        :content="
          $textDictionary['softwareCenter.initialView.headline'] ??
          'Latest Releases'
        "
      />
      <TagList
        v-else
        :tags="formattedFilterFacets"
        :is-small="true"
        style-type="secondary"
        @close="removeTag"
      />
    </template>
    <template #list>
      <AlgoliaSearchBanners :banners="searchBanners" utm-source="software" />
      <div
        v-if="
          !isListLoading && !(store.softwareDownloads?.results?.length ?? 0)
        "
        class="text rte-text"
      >
        <LibRichTextRenderer :rich-text="noResultsRichText" />
      </div>
      <DownloadCards
        size="three-quarters"
        :items="formattedData"
        :is-loading="isListLoading"
        @open="openModal"
      />
      <Button
        v-if="store.canLoadMore"
        style-type="link"
        icon-type="icon-left"
        icon-name="arrow-down"
        @click="store.fetchMoreSoftwareDownloads"
      >
        {{
          $textDictionary['softwareCenter.showMoreButton.label'] ?? 'Show more'
        }}
      </Button>
    </template>
  </ProductListLayout>
  <SoftwareCenterModalContainer />
</template>

<script setup lang="ts">
import { useDownloadStore } from '~/stores/downloadStore/softwareDownloadStore';
import {
  ProductListLayout,
  TagList,
  DownloadCards,
  HeadlineModule,
  Button
} from '@hypercodestudio/basler-components';
import SoftwareCenterModalContainer from './SoftwareCenterModalContainer.vue';
import SoftwareCenterFilters from '~/components/downloads/software/SoftwareCenterFilters.vue';
import { useSoftwareListData } from '~/composables/useSoftwareListData';
import { useDownloadCenterSearchRedirect } from '~/composables/useDownloadCenterSearchRedirect';
import { getDownloadCenterSearchQuery } from '~/utils/algolia/getDownloadCenterSearchQuery';
import AlgoliaSearchBanners from '~/components/global/lib/Search/AlgoliaSearchBanners.vue';
import { useSearchBanners } from '~/composables/useSearchBanners';
import { filtersRecordEqual } from '~/stores/downloadStore/helper/filtersRecordEqual';
import type { LocationQuery, LocationQueryValue } from 'vue-router';

const route = useRoute();
const store = useDownloadStore();

// Reset shared store state (PDP software tab, global search) before route watchers
// run. We can not use $reset here: modal detail URLs remount this component via
// the router and must not wipe unrelated state. Route watchers restore
// query/filters from the URL immediately after.
// @see components/downloads/documents/DocumentCenter.vue (DBP-1237, DBP-3276)
store.resetForSoftwareCenterEntry();

const { checkAndApplyDownloadCenterSearchRedirect } =
  useDownloadCenterSearchRedirect('software');

const { $textDictionary, $globalPageSettings, $activeLocale } = useNuxtApp();

const softwareCenterStartSlug = computed(
  () => $globalPageSettings.value?.softwareCenterPage?.slug
);

const {
  softwareCountInformation,
  noResultsRichText,
  formattedFilterFacets,
  formattedData,
  removeTag
} = useSoftwareListData((softwareItem, searchParams) =>
  buildUrlString(
    $activeLocale.value.webapp,
    `${softwareCenterStartSlug.value}/${softwareItem.id}`,
    searchParams.toString()
  )
);

const searchBanners = useSearchBanners(
  () => store.softwareDownloads?.searchBanners
);

const router = useRouter();

const isListLoading = computed(
  () =>
    store.loading === true &&
    (store.softwareDownloads?.results?.length ?? 0) === 0
);

async function openModal(downloadId: string): Promise<void> {
  await store.openModal(downloadId);

  if (!import.meta.client) {
    return;
  }

  const targetLocation = {
    path: buildUrlString(
      $activeLocale.value.webapp,
      `${softwareCenterStartSlug.value}/${downloadId}`
    ),
    ...store.createUrlParams()
  };
  const targetHref = router.resolve(targetLocation).href;
  if (window.location.pathname + window.location.search !== targetHref) {
    window.history.pushState(window.history.state, '', targetHref);
  }
}

const hasLoadedFilters = ref(false);
watch(
  () => store.softwareDownloads?.filters?.length,
  (length) => {
    if (length) {
      hasLoadedFilters.value = true;
    }
  },
  { immediate: true }
);

async function syncSoftwareCenterFromQuery(routeQuery: LocationQuery) {
  const previousSearchQuery = store.query;
  const previousFilters = store.filters;

  if (store.shouldReuseSoftwareCenterList(routeQuery)) {
    return;
  }

  const searchQuery = getDownloadCenterSearchQuery(routeQuery);
  store.syncSearchQueryFromRoute(routeQuery);

  if (searchQuery) {
    const redirectApplied =
      await checkAndApplyDownloadCenterSearchRedirect(searchQuery);
    if (redirectApplied) {
      return;
    }
  }

  store.updateFiltersFromRoute(routeQuery);

  const didSearchQueryChange = previousSearchQuery !== store.query;
  const didFiltersChange = !filtersRecordEqual(previousFilters, store.filters);

  // Query-only route changes (e.g. browser back/forward, deep-links) do not
  // trigger the filters watcher and therefore need an explicit fetch.
  if (didSearchQueryChange && !didFiltersChange) {
    void store.fetchSoftwareDownloads();
  }
}

function toLocationQueryFromSearch(search: string): LocationQuery {
  const searchParams = new URLSearchParams(search);
  const query: Record<string, LocationQueryValue | LocationQueryValue[]> = {};

  for (const [key, value] of searchParams.entries()) {
    const existingValue = query[key];

    if (!existingValue) {
      query[key] = value;
      continue;
    }

    if (Array.isArray(existingValue)) {
      existingValue.push(value);
      continue;
    }

    query[key] = [existingValue, value];
  }

  return query;
}

async function handleBrowserHistoryChange(): Promise<void> {
  if (!import.meta.client) {
    return;
  }

  const locationQuery = toLocationQueryFromSearch(window.location.search);
  await syncSoftwareCenterFromQuery(locationQuery);
  await store.handlePathnameChange(window.location.pathname);
}

watch(
  () => route.query,
  async (routeQuery) => {
    await syncSoftwareCenterFromQuery(routeQuery);
  },
  { immediate: true }
);

watch(
  () => store.filters,
  (newFilters, oldFilters) => {
    if (
      (store.softwareDownloads == null && oldFilters == null) ||
      // Route changes for a download detail modal do not change filters; ignore
      // the immediate run after remount when list data is already in the store.
      (oldFilters != null && !filtersRecordEqual(oldFilters, newFilters))
    ) {
      void store.fetchSoftwareDownloads();
    }
  },
  { immediate: true, deep: false }
);

watch([() => route.params], () => store.handleRouteChange(route.params), {
  immediate: true
});

onMounted(() => {
  window.addEventListener('popstate', handleBrowserHistoryChange);
});

onBeforeUnmount(() => {
  window.removeEventListener('popstate', handleBrowserHistoryChange);
});
</script>
